 import React, { useState, useEffect, useMemo } from "react";
 import ProductForm from '../Products/ProductForm';
import { doc, updateDoc, setDoc, addDoc, collection, getDoc, getDocs, deleteDoc, runTransaction, serverTimestamp, query, orderBy, limit, startAfter, where } from "firebase/firestore";
import { db } from "../../firebase";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useProducts } from '../../context/ProductsContext';
import { getNegativeStockProducts } from '../../lib/inventoryAlerts';

// ==== Helpers robustos ====

// — Unidades legibles en inventario físico —
const getUnitCode = (p) => {
  const u = String(p?.unit || p?.unidad || '').trim().toLowerCase();
  if (!u) return 'U';                                // por defecto: unidades
  if (/^g(r|ramo|ramos)?$/.test(u)) return 'g';      // g, gr, gramo(s)
  if (/^kg$/.test(u)) return 'g';                    // si usas kg, puedes convertir aparte
  if (/^(ml|cc|mililitro|mililitros)$/.test(u)) return 'ml';
  if (/^(l|lt|litro|litros)$/.test(u)) return 'ml';  // si usas litros, puedes convertir aparte
  if (/^u(nid(ad(es)?)?)?$/.test(u)) return 'U';     // u, und, unidad(es)
  return 'U';
};

// arriba del componente, o junto a otros helpers
const showUser = (u) => {
  if (typeof u === 'string') return u;
  if (!u) return '—';
  return u.displayName || u.username || u.name || u.email || u.uid || '—';
};


const formatQtyWithUnit = (p, qty) => {
  const code = getUnitCode(p);               // 'g' | 'ml' | 'U'
  const n = Number(qty || 0);
  // Nota: si quieres convertir kg→g o L→ml, hazlo aquí.
  const suffix = code === 'U' ? 'U' : code.toUpperCase();
  return `${n.toLocaleString('es-CO')} ${suffix}`;
};

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);



const isObj = (v) => v && typeof v === "object";

const isLikelyIcecreamByName = (p) => {
  const name = String(p?.name || "").toLowerCase();
  // si menciona “sabor/sabores” lo tratamos como helado (producto final)
  if (/\b(sabor|sabores)\b/.test(name)) return true;
  // términos típicos de heladería, pero solo si NO es insumo
  const t = String(p?.type || "").toLowerCase();
  if (t !== "insumo" && /(cono|barquillo|sundae|malteada|milkshake)/.test(name)) return true;
  return false;
};

const localISODate = (d = new Date()) => {
  const tz = d.getTimezoneOffset();                  // minutos
  const local = new Date(d.getTime() - tz * 60000);  // corrige a hora local
  return local.toISOString().slice(0, 10);           // YYYY-MM-DD
};


const coerceBool = (v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number")  return v !== 0;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return ["true","1","yes","y","si","sí","on","enabled"].includes(s);
  }
  return false;
};

// $59.500 en COP
const moneyCOP = (n) =>
  Number(n || 0).toLocaleString('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });


// Detecta cualquier producto marcado/guardado como helado (sabores variables)
const isMarkedAsIcecream = (p) => {
  const cfg = p?.config || {};
  const ic  = cfg.icecream ?? cfg.iceCream ?? cfg.ice_cream ?? cfg.helado
           ?? p?.icecream ?? p?.iceCream ?? p?.ice_cream ?? p?.helado;

  const asText = (v) => (v == null ? "" : String(v)).toLowerCase();

  return (
    // flag directo
    coerceBool(ic) ||
    // objeto presente (config propia de helado)
    isObj(ic) ||
    // strings típicos que graban algunos formularios
    /ice|helad|sabor|flavor|variable/.test(asText(ic)) ||
    // modos de receta usados para helados
    /ice|helad|choice|backflush/.test(asText(p?.recipeMode)) ||
    // categoría “Helados”
    /helad/.test(asText(p?.category || p?.categoria))
  );
};

const hasAnyKey = (obj, keys) => !!obj && keys.some(k => k in obj);

function isIceCreamProduct(p) {
   if (isLikelyIcecreamByName(p)) return true; // ⬅️ fallback por nombre
  const cfg = p?.config || {};
  const ic = cfg.icecream ?? cfg.iceCream ?? cfg.ice_cream ?? cfg.helado
           ?? p?.icecream ?? p?.iceCream ?? p?.ice_cream ?? p?.helado;

  // 1) flag booleano/num/string
  if (coerceBool(ic)) return true;

  // 2) objeto con señales típicas
  if (isObj(ic) && (
    coerceBool(ic.enabled) ||
    hasAnyKey(ic, ["base","baseId","baseProduct","baseProductId"]) ||
    hasAnyKey(ic, ["numFlavors","flavors","sabores"]) ||
    hasAnyKey(ic, ["gramsPerFlavor","gramsPerSabor"]) ||
    hasAnyKey(ic, ["servilletasPorVenta","spoonsPerSale","cucharasPorVenta"])
  )) return true;

  // 3) flags sueltos
  if (coerceBool(p?.icecreamEnabled) || coerceBool(p?.isIceCream) || coerceBool(p?.isIcecream))
    return true;

  // 4) modo/receta
  const mode = String(p?.recipeMode || p?.mode || "").toLowerCase();
  if (["icecream","helado","ice","choice","choices","choicegroups","backflush"].some(x => mode.includes(x)))
    return true;

  // 5) categoría (último recurso)
  const cat = (p?.category || p?.categoria || "").toLowerCase();
  if (cat.includes("helad")) return true;

  return false;
}


 const InventoryView = ({ onBack, products, setProducts, currentUser }) => {
  const { loadAllProductsOnce } = useProducts();
    const [ajusteFiltroUsuario, setAjusteFiltroUsuario] = useState('');
const [ajusteFiltroFecha, setAjusteFiltroFecha] = useState('');
const [ajusteFiltroTipo, setAjusteFiltroTipo] = useState(''); // '' = todos
const [paginaAjustes, setPaginaAjustes] = useState(1);
const ajustesPorPagina = 30;

  const [currentSubView, setCurrentSubView] = useState('control'); // 'control', 'adjustments', 'losses', 'transfers', 'editProduct', 'physicalInventory'
  const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
  const [adjustmentData, setAdjustmentData] = useState({
    productId: '',
    quantity: '',
    type: 'ajuste', // 'ajuste', 'perdida', 'transferencia'
    reason: '',
    user: currentUser,
date: localISODate(),
    destinationProductId: '' // Nuevo campo para transferencias
  });
  const [editingProduct, setEditingProduct] = useState(null); // Para editar producto en línea
  const [searchTerm, setSearchTerm] = useState('');
  const [invTypeFilter, setInvTypeFilter] = useState('todos'); // 'todos' | 'venta' | 'insumos'
  const [inventorySort, setInventorySort] = useState({ key: 'name', direction: 'asc' });
  const INVENTORY_ITEMS_PER_PAGE = 30;
  const [inventoryPage, setInventoryPage] = useState(1);

  useEffect(() => {
    loadAllProductsOnce();
  }, [loadAllProductsOnce]);

  // Mocks de historial (en un sistema real, esto vendría de la base de datos)
  const [adjustmentsHistory, setAdjustmentsHistory] = useState([]);
  const [loadingAjustes, setLoadingAjustes] = useState(false);
  const [lossesHistory, setLossesHistory] = useState([]);
  const [transfersHistory, setTransfersHistory] = useState([]);

  // Mock de stock (se actualizará con los ajustes)
const [productStocks, setProductStocks] = useState({});

useEffect(() => {
  const map = {};
  (products || []).forEach(p => { map[p.id] = Number(p.stock || 0); });
  setProductStocks(map);
}, [products]);


  // Estado para Inventario Físico
  const [physicalCounts, setPhysicalCounts] = useState({});
  const [physicalHistory, setPhysicalHistory] = useState([]);
  const [loadingPhysicalHistory, setLoadingPhysicalHistory] = useState(false);
  const [expandedPhysicalEvent, setExpandedPhysicalEvent] = useState(null);
  const [lastPhysicalInventory, setLastPhysicalInventory] = useState(null); // Info del último inventario
  const [lastPhysicalCountsData, setLastPhysicalCountsData] = useState({}); // Datos del último conteo por producto
  const [physicalSearchTerm, setPhysicalSearchTerm] = useState('');
  const [physicalCountMode, setPhysicalCountMode] = useState('partial');
  const [physicalReview, setPhysicalReview] = useState(null);
  const [savingPhysicalInventory, setSavingPhysicalInventory] = useState(false);


  
const fetchUltimoInventario = async () => {
  const docSnap = await getDoc(doc(db, "ultimoInventarioFisico", "ultimo"));
  if (docSnap.exists()) {
    setLastPhysicalInventory(docSnap.data());
    setLastPhysicalCountsData(docSnap.data().counts || {});
  }
};

const fetchPhysicalHistory = async () => {
  setLoadingPhysicalHistory(true);
  try {
    const snap = await getDocs(
      query(
        collection(db, "historialInventarioFisico"),
        orderBy("createdAt", "desc"),
        limit(30)
      )
    );
    const events = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setPhysicalHistory(events);
  } catch (error) {
    console.error("Error al cargar historial de inventario físico:", error);
  } finally {
    setLoadingPhysicalHistory(false);
  }
};

const exportPhysicalHistoryToCSV = () => {
  if (!physicalHistory.length) {
    toast.warn("No hay historial para exportar.");
    return;
  }
  const headers = [
    "Fecha",
    "Usuario",
    "Producto",
    "Stock sistema",
    "Conteo físico",
    "Diferencia",
    "Costo unitario",
    "Valor diferencia",
    "Estado",
    "Total sobrante evento",
    "Total faltante evento"
  ];

  const rows = [];
  physicalHistory.forEach((evt) => {
    const items = evt.items || [];
    items.forEach((item) => {
      rows.push([
        evt.date || "",
        evt.user || "",
        item.productName || "",
        item.systemStock ?? "",
        item.physicalStock ?? "",
        item.difference ?? "",
        item.unitCost ?? "",
        item.diffValue ?? "",
        item.difference > 0 ? "Sobrante" : item.difference < 0 ? "Faltante" : "Igual",
        evt.totalSobrante ?? "",
        evt.totalFaltante ?? ""
      ]);
    });
  });

  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csvContent = [headers.map(escape).join(";"), ...rows.map((r) => r.map(escape).join(";"))].join("\n");
  const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "historial_inventario_fisico.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const AJUSTES_PAGE = 100;
const [ajustesCursor, setAjustesCursor] = useState(null);
const [hasMoreAjustes, setHasMoreAjustes] = useState(false);

const fetchAjustes = async (append = false) => {
  setLoadingAjustes(true);
  try {
    let q = query(collection(db, "ajustesInventario"), orderBy("createdAt", "desc"), limit(AJUSTES_PAGE));
    if (append && ajustesCursor) {
      q = query(collection(db, "ajustesInventario"), orderBy("createdAt", "desc"), startAfter(ajustesCursor), limit(AJUSTES_PAGE));
    }
    const snapshot = await getDocs(q);
    const history = snapshot.docs.map((docu) => {
      const d = docu.data();
      return { id: docu.id, ...d, user: showUser(d.user) };
    });
    setAdjustmentsHistory((prev) => append ? [...prev, ...history] : history);
    const last = snapshot.docs[snapshot.docs.length - 1] || null;
    setAjustesCursor(last);
    setHasMoreAjustes(snapshot.docs.length === AJUSTES_PAGE);
  } catch (err) {
    console.error('No se pudieron cargar ajustes', err);
    if (!append) {
      setAdjustmentsHistory([]);
    }
  } finally {
    setLoadingAjustes(false);
  }
};
useEffect(() => {
  fetchUltimoInventario();
  fetchAjustes(false);
  fetchPhysicalHistory();
}, []);
useEffect(() => {
  (products || [])
    .filter(p => /cono|helad/i.test(p?.name || ""))
    .forEach(() => {});
}, [products]);

  const handlePhysicalCountChange = (productId, rawCount) => {
    setPhysicalReview(null);
    setPhysicalCounts((prev) => {
      const next = { ...prev };
      if (rawCount === '') {
        delete next[productId];
        return next;
      }
      const count = Number(rawCount);
      if (Number.isFinite(count) && count >= 0) next[productId] = count;
      return next;
    });
  };

const handleComparePhysicalInventory = () => {
  const countedProducts = physicalListUnique.filter((product) => hasOwn(physicalCounts, product.id));
  const pendingCount = physicalListUnique.length - countedProducts.length;

  if (countedProducts.length === 0) {
    toast.error('Registra al menos un conteo. Los campos vacíos se consideran productos sin contar.');
    return;
  }
  if (physicalCountMode === 'complete' && pendingCount > 0) {
    toast.error(`Faltan ${pendingCount} productos por contar. Usa conteo parcial si solo ajustarás algunos.`);
    return;
  }

  const items = countedProducts.map((product) => {
    const systemStock = Number(product.stock || 0);
    const physicalStock = Number(physicalCounts[product.id]);
    const difference = physicalStock - systemStock;
    const unitCost = Number(product.costPrice || 0);
    return {
      productId: String(product.id),
      productName: product.name || '',
      systemStock,
      physicalStock,
      difference,
      unitCost,
      diffValue: difference * unitCost,
      status: difference > 0 ? 'sobrante' : difference < 0 ? 'faltante' : 'igual',
    };
  });
  const differences = items.filter((item) => item.difference !== 0);
  if (differences.length > 240) {
    toast.error('El lote supera 240 ajustes. Divide el conteo en grupos más pequeños para procesarlo con seguridad.');
    return;
  }

  setPhysicalReview({
    mode: physicalCountMode,
    items,
    differences,
    countedCount: countedProducts.length,
    pendingCount,
    increaseCount: differences.filter((item) => item.difference > 0).length,
    decreaseCount: differences.filter((item) => item.difference < 0).length,
    zeroCount: differences.filter((item) => item.physicalStock === 0).length,
    totalSobrante: differences.filter((item) => item.difference > 0).reduce((sum, item) => sum + item.diffValue, 0),
    totalFaltante: differences.filter((item) => item.difference < 0).reduce((sum, item) => sum + Math.abs(item.diffValue), 0),
  });
};

const confirmPhysicalInventoryAdjustment = async () => {
  if (!physicalReview || savingPhysicalInventory) return;
  setSavingPhysicalInventory(true);
  const eventRef = doc(collection(db, 'historialInventarioFisico'));
  const latestRef = doc(db, 'ultimoInventarioFisico', 'ultimo');
  const date = localISODate();
  const actor = showUser(currentUser);

  try {
    await runTransaction(db, async (tx) => {
      const productRefs = physicalReview.items.map((item) => doc(db, 'productos', item.productId));
      const snapshots = await Promise.all(productRefs.map((ref) => tx.get(ref)));

      snapshots.forEach((snapshot, index) => {
        const item = physicalReview.items[index];
        if (!snapshot.exists()) throw new Error(`El producto ${item.productName} ya no existe.`);
        if (Number(snapshot.data()?.stock || 0) !== item.systemStock) {
          throw new Error(`Cambió el stock de ${item.productName}. Vuelve a comparar antes de ajustar.`);
        }
      });

      physicalReview.differences.forEach((item) => {
        const adjustmentRef = doc(collection(db, 'ajustesInventario'));
        tx.update(doc(db, 'productos', item.productId), { stock: item.physicalStock, updatedAt: serverTimestamp() });
        tx.set(adjustmentRef, {
          date,
          createdAt: serverTimestamp(),
          user: actor,
          action: 'Ajuste por inventario físico',
          productId: item.productId,
          productName: item.productName,
          type: item.difference > 0 ? 'ajuste' : 'perdida',
          quantity: Math.abs(item.difference),
          previousStock: item.systemStock,
          newStock: item.physicalStock,
          batchId: eventRef.id,
          reason: `Conteo físico ${physicalReview.mode === 'complete' ? 'completo' : 'parcial'}`,
        });
      });

      const eventPayload = {
        date,
        createdAt: serverTimestamp(),
        user: actor,
        mode: physicalReview.mode,
        result: physicalReview.differences.length ? 'Ajustado con revisión previa' : 'Sin diferencias',
        items: physicalReview.items,
        countedCount: physicalReview.countedCount,
        pendingCount: physicalReview.pendingCount,
        totalSobrante: physicalReview.totalSobrante,
        totalFaltante: physicalReview.totalFaltante,
      };
      tx.set(eventRef, eventPayload);
      tx.set(latestRef, { ...eventPayload, eventId: eventRef.id, counts: { ...physicalCounts } });
    });

    if (setProducts && physicalReview.differences.length) {
      const stocks = new Map(physicalReview.differences.map((item) => [item.productId, item.physicalStock]));
      setProducts((prev = []) => prev.map((product) =>
        stocks.has(String(product.id)) ? { ...product, stock: stocks.get(String(product.id)) } : product
      ));
    }
    const adjustedCount = physicalReview.differences.length;
    setLastPhysicalCountsData({ ...physicalCounts });
    setPhysicalCounts({});
    setPhysicalReview(null);
    await Promise.all([fetchUltimoInventario(), fetchAjustes(false), fetchPhysicalHistory()]);
    toast.success(adjustedCount ? 'Inventario ajustado correctamente.' : 'Conteo guardado sin diferencias.');
  } catch (error) {
    console.error('Error al aplicar inventario físico:', error);
    toast.error(error?.message || 'No se pudo aplicar el inventario físico.');
  } finally {
    setSavingPhysicalInventory(false);
  }
};
const normType = (p) => (p?.type || 'final').toLowerCase();
const matchesSearch = (p) =>
  (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
  (p.barcode || '').toLowerCase().includes((searchTerm || '').toLowerCase());

const { saleProducts, supplyProducts } = useMemo(() => {
 const base = (products || []).filter(matchesSearch);
 return {
    // "Todos" (para vender) sin filtrar, por si quieres seguir viéndolos en la pestaña "Todos"
    saleProducts: base.filter(p => inferType(p) !== 'insumo'),
   supplyProducts: base.filter(p => inferType(p) === 'insumo'),
  };
}, [products, searchTerm]);

const negativeStockProducts = useMemo(() => getNegativeStockProducts(products), [products]);

  // ✅ Lo que se muestra realmente cuando eliges "Para vender"
const ventaMostrados = useMemo(() => {
  return (products || [])
    .filter(matchesSearch)
    .filter(p => inferType(p) !== 'insumo') // solo cosas para vender
    .filter(p => !usesSupplies(p))          // ⬅️ oculta helados/recetas que consumen insumos
    .filter(p => Number(p.stock ?? 0) !== 0); // conserva visibles los saldos negativos
}, [products, searchTerm]);

const insumosContabilizados = useMemo(() => {
  return (products || [])
    .filter(matchesSearch)
    .filter(p => inferType(p) === 'insumo')
    // Los faltantes de recetas quedan visibles hasta que una entrada los compense.
    .filter(p => Number(p.stock ?? 0) !== 0);
}, [products, searchTerm]);



const inventoryItemsForPagination = useMemo(() => {
    const available = (product) => inferType(product) === 'receta'
      ? computeRecipeAvailable(product)
      : Number(product?.stock || 0);
    const valueFor = (product) => {
      switch (inventorySort.key) {
        case 'barcode': return String(product?.barcode || '');
        case 'available': return available(product);
        case 'price': return Number(product?.price || 0);
        case 'costPrice': return Number(product?.costPrice || 0);
        case 'stockValue': return available(product) * Number(product?.costPrice || 0);
        case 'name':
        default: return String(product?.name || '');
      }
    };
    const compareProducts = (left, right) => {
      const leftValue = valueFor(left);
      const rightValue = valueFor(right);
      let result = typeof leftValue === 'number' && typeof rightValue === 'number'
        ? leftValue - rightValue
        : String(leftValue).localeCompare(String(rightValue), 'es', { sensitivity: 'base', numeric: true });
      if (result === 0) {
        result = String(left?.name || '').localeCompare(String(right?.name || ''), 'es', { sensitivity: 'base', numeric: true });
      }
      return inventorySort.direction === 'desc' ? -result : result;
    };
    const sortedEntries = (items, kind) => [...items].sort(compareProducts).map((product) => ({ kind, product }));
    const saleEntries = invTypeFilter !== 'insumos' ? sortedEntries(ventaMostrados, 'venta') : [];
    const supplyEntries = invTypeFilter !== 'venta' ? sortedEntries(insumosContabilizados, 'insumos') : [];
    return [...saleEntries, ...supplyEntries];
  }, [invTypeFilter, inventorySort, ventaMostrados, insumosContabilizados]);

  const totalInventoryItems = inventoryItemsForPagination.length;
  const totalInventoryPages = Math.max(1, Math.ceil((totalInventoryItems || 0) / INVENTORY_ITEMS_PER_PAGE));

  const pagedInventoryItems = useMemo(() => {
    const start = (inventoryPage - 1) * INVENTORY_ITEMS_PER_PAGE;
    return inventoryItemsForPagination.slice(start, start + INVENTORY_ITEMS_PER_PAGE);
  }, [inventoryItemsForPagination, inventoryPage]);

  const ventaPaginados = useMemo(
    () => pagedInventoryItems.filter(entry => entry.kind === 'venta').map(entry => entry.product),
    [pagedInventoryItems]
  );
  const supplyPaginados = useMemo(
    () => pagedInventoryItems.filter(entry => entry.kind === 'insumos').map(entry => entry.product),
    [pagedInventoryItems]
  );
  const inventoryRangeStart = totalInventoryItems === 0 ? 0 : (inventoryPage - 1) * INVENTORY_ITEMS_PER_PAGE + 1;
  const inventoryRangeEnd = totalInventoryItems === 0 ? 0 : Math.min(totalInventoryItems, inventoryPage * INVENTORY_ITEMS_PER_PAGE);

  useEffect(() => {
    setInventoryPage(prev => Math.min(prev, totalInventoryPages));
  }, [totalInventoryPages]);

  useEffect(() => {
    setInventoryPage(1);
  }, [invTypeFilter, searchTerm, inventorySort]);

function hasRecipeConfig(p) {
  if (p?.config?.promotion?.enabled === true) return true;
  const r = p?.config?.recipe;
  if (coerceBool(r?.enabled)) return true;
  if ((r?.baseItems?.length ?? 0) > 0) return true;
  if ((r?.choiceGroups?.length ?? 0) > 0) return true;

  // Otras formas frecuentes de modelar recetas/insumos
  if ((p?.ingredients?.length ?? 0)   > 0) return true;
  if ((p?.insumos?.length ?? 0)       > 0) return true;   // español
  if ((p?.components?.length ?? 0)    > 0) return true;
  if ((p?.componentes?.length ?? 0)   > 0) return true;   // español
  if ((p?.recipeItems?.length ?? 0)   > 0) return true;
  if ((p?.comboItems?.length ?? 0)    > 0) return true;
  if ((p?.formula?.length ?? 0)       > 0) return true;

  return false;
}


// usa insumos si es receta o si es helado
function usesSupplies(p) {
  return hasRecipeConfig(p) || isIceCreamProduct(p);
}


function inferType(p) {
  const t = (p?.type || "").toLowerCase();
  if (t === "insumo") return "insumo";
  if (usesSupplies(p)) return "receta";
  // ⬇️ Mantén explícitamente los productos finales aunque no se detecte receta
  if (t === "final" || t === "venta" || t === "producto final") return "final";
  return "simple";
}


function inferRecipeMode(p) {
  return inferType(p) === 'receta' ? (p.recipeMode || 'backflush') : null;
 }
function isRecipeBackflush(p) {
  return inferType(p) === 'receta' && inferRecipeMode(p) === 'backflush';
 }
 
 // ---- Mostrar disponible con unidad (usa inferType local) ----
const displayAvailable = (item, available) => {
  const isRecipe = inferType(item) === 'receta';
  const n = Number(available || 0);
  return isRecipe ? `${n.toLocaleString('es-CO')} U` : formatQtyWithUnit(item, n);
};
 
 
 // Mapa id -> producto (para leer stock de insumos rápidamente)
const productsById = useMemo(() => {
  const m = {};
  (products || []).forEach(p => { m[p.id] = p; });
  return m;
}, [products]);

// debajo de tus otros useMemo / helpers
const physicalList = useMemo(() => {
  return (products || [])
    .filter(p => {
      const t = (p?.type || "").toLowerCase();

      // Excluir recetas/helados (usan insumos). Solo mostrar si son INSUMO.
      if (usesSupplies(p)) return t === "insumo";

      // Mostrar insumos y productos simples/finales que NO usan insumos
      return t === "insumo" || (t !== "insumo" && !usesSupplies(p));
    })
    .sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "es", { sensitivity: "base" })
    );
}, [products]);

const physicalListUnique = useMemo(() => {
  const seen = new Set();
  const list = [];
  for (const p of physicalList) {
    const key = p?.id || p?.barcode || p?.name;
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    list.push(p);
  }
  return list;
}, [physicalList]);

const filteredPhysicalList = useMemo(() => {
  const term = String(physicalSearchTerm || '').toLowerCase().trim();
  if (!term) return physicalListUnique;
  return physicalListUnique.filter((product) =>
    String(product?.name || '').toLowerCase().includes(term) ||
    String(product?.barcode || '').toLowerCase().includes(term)
  );
}, [physicalListUnique, physicalSearchTerm]);

const exportPhysicalComparisonToExcel = () => {
  const rows = filteredPhysicalList.map((product) => {
    const wasCounted = hasOwn(physicalCounts, product.id);
    const currentCount = wasCounted ? Number(physicalCounts[product.id]) : '';
    const systemStock = Number(product.stock || 0);
    const difference = wasCounted ? Number(currentCount) - systemStock : '';
    return {
      producto: product.name || '',
      unidad: getUnitCode(product),
      stockSistema: systemStock,
      conteoFisicoActual: currentCount,
      diferencia: difference,
    };
  });

  if (rows.length === 0) {
    toast.warn('No hay productos para exportar.');
    return;
  }

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const date = localISODate();
  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:x="urn:schemas-microsoft-com:office:excel"
          xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="UTF-8" />
        <style>
          body { font-family: Calibri, Arial, sans-serif; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #cbd5e1; padding: 8px 10px; }
          th { background: #1d4ed8; color: #ffffff; font-weight: 700; text-align: center; }
          td { background: #ffffff; }
          .title { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
          .meta { color: #475569; margin-bottom: 12px; }
          .text { text-align: left; }
          .center { text-align: center; }
          .num { text-align: right; }
        </style>
      </head>
      <body>
        <div class="title">Inventario Fisico y Comparacion</div>
        <div class="meta">Fecha de exportacion: ${escapeHtml(date)}</div>
        <table>
          <thead>
            <tr>
              <th>Producto</th>
              <th>Unidad</th>
              <th>Stock sistema</th>
              <th>Conteo fisico actual</th>
              <th>Diferencia</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map((row) => `
              <tr>
                <td class="text">${escapeHtml(row.producto)}</td>
                <td class="center">${escapeHtml(row.unidad)}</td>
                <td class="num">${escapeHtml(row.stockSistema)}</td>
                <td class="num">${escapeHtml(row.conteoFisicoActual)}</td>
                <td class="num">${escapeHtml(row.diferencia)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
    </html>
  `;

  const blob = new Blob(["\ufeff" + html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `inventario_fisico_actual_${date}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Lista de productos válida para AJUSTE (excluye recetas/helados que usan insumos)
const productsForAdjustment = useMemo(() => {
  return (products || [])
    .filter(p => {
      const t = (p?.type || "").toLowerCase();
      // Excluir recetas/helados (usan insumos) salvo que sean INSUMO
      if (usesSupplies(p)) return t === "insumo";
      return true;
    })
    .sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "es", { sensitivity: "base" })
    );
}, [products]);

const adjustmentOptions = useMemo(() => {
  const seen = new Set();
  const list = [];
  for (const p of productsForAdjustment) {
    const key = p?.id || p?.barcode || p?.name;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    list.push(p);
  }
  return list;
}, [productsForAdjustment]);


// Ítems que SÍ cuentan para KPIs/valor:
// - Insumos con stock > 0
// - Excluye recetas/compuestos
// - Finales solo si trackean stock y tienen stock > 0
const countables = useMemo(() => {
  return (products || []).filter(p => {
    const stock = Number(p?.stock || 0);
    if (stock <= 0) return false;
    // Excluir recetas/helados/backflush (consumen insumos)
    if (inferType(p) === 'receta') return false;
    // Incluir insumos y cualquier producto simple/final con stock
    return true;
  });
}, [products]);

const inventoryTotal = useMemo(() => {
  return countables.reduce((sum, p) =>
    sum + Number(p.stock || 0) * Number(p.costPrice || 0),
  0);
}, [countables]);

const itemsCount = countables.length;

const missingCost = useMemo(() => {
  return countables.filter(p =>
    Number(p.stock || 0) > 0 && !Number(p.costPrice || 0)
  );
}, [countables]);

const productsSorted = useMemo(() => {
  return [...(products || [])].sort((a, b) =>
    String(a?.name || "").localeCompare(String(b?.name || ""), "es", { sensitivity: "base" })
  );
}, [products]);


// Calcula unidades disponibles para una receta backflush según sus ingredientes base
function computeRecipeAvailable(product) {
  const base = product?.config?.recipe?.baseItems || [];
  if (!base.length) return 0;

  let minUnits = Infinity;
  for (const it of base) {
    const ingId = it.insumoId || it.productId;
    const per   = Number(it.qtyPerUnit || it.qty || 0);
    if (!ingId || per <= 0) continue;
    const ing = productsById[ingId];
    const ingStock = Number(ing?.stock ?? 0);
    const units = Math.floor(ingStock / per);
    minUnits = Math.min(minUnits, units);
  }
  return Number.isFinite(minUnits) ? minUnits : 0;
}

const toggleInventorySort = (key) => {
  setInventorySort((current) => ({
    key,
    direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
  }));
};

const renderSortHeader = (label, key) => {
  const active = inventorySort.key === key;
  return (
    <th
      key={key}
      scope="col"
      aria-sort={active ? (inventorySort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
    >
      <button
        type="button"
        onClick={() => toggleInventorySort(key)}
        className={`inline-flex items-center gap-2 whitespace-nowrap transition hover:text-sky-700 ${active ? 'text-sky-700' : ''}`}
        title="Haz clic para ordenar; vuelve a hacer clic para invertir el orden"
      >
        <span>{label}</span>
        <span aria-hidden="true">{active ? (inventorySort.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
      </button>
    </th>
  );
};


const renderRow = (item, keyPrefix = '') => {
  const isRecipe = inferType(item) === 'receta';
  const disponible = isRecipe ? computeRecipeAvailable(item) : Number(item.stock ?? 0);

  const t = (item?.type || 'simple').toLowerCase();
  const chip = isRecipe ? 'Receta' : (t === 'insumo' ? 'Insumo' : 'Simple');
  const chipClass = isRecipe
    ? 'bg-purple-100 text-purple-700'
    : (t === 'insumo' ? 'bg-gray-100 text-gray-700' : 'bg-blue-100 text-blue-700');
  return (
<tr key={`${keyPrefix}${item.id}`} className="hover:bg-gray-50 transition duration-150">
        <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          {item.image && <img src={item.image} alt={item.name} className="h-12 w-12 object-cover rounded-md shadow-sm mr-3" />}
          <div>
            <div className="text-sm font-medium text-gray-900">{item.name}</div>
            <span className={`inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full ${chipClass}`}>{chip}</span>
          </div>
        </div>
      </td>

      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.barcode || '—'}</td>

<td
  className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${
    disponible <= 0 ? 'text-red-600' : 'text-green-600'
  }`}
>
  {displayAvailable(item, disponible)}
</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {typeof item.price === 'number' ? moneyCOP(item.price) : '—'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {typeof item.costPrice === 'number' ? moneyCOP(item.costPrice) : 'N/A'}
      </td>

<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {moneyCOP(Number(disponible) * Number(item.costPrice || 0))}
      </td>

    </tr>
  );
};

const renderMobileInventoryCard = (item, keyPrefix = '') => {
  const isRecipe = inferType(item) === 'receta';
  const disponible = isRecipe ? computeRecipeAvailable(item) : Number(item.stock ?? 0);
  const t = (item?.type || 'simple').toLowerCase();
  const chip = isRecipe ? 'Receta' : (t === 'insumo' ? 'Insumo' : 'Simple');
  const chipClass = isRecipe
    ? 'bg-purple-100 text-purple-700'
    : (t === 'insumo' ? 'bg-gray-100 text-gray-700' : 'bg-blue-100 text-blue-700');

  return (
    <article key={`${keyPrefix}${item.id}`} className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        {item.image && <img src={item.image} alt={item.name} className="h-14 w-14 rounded-xl object-cover shadow-sm" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="truncate text-base font-bold text-slate-900">{item.name}</h3>
              <p className="mt-1 text-xs text-slate-500">{item.barcode || 'Sin codigo'}</p>
            </div>
            <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${chipClass}`}>{chip}</span>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 rounded-2xl bg-slate-50 p-3 text-sm">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Disponible</p>
          <p className={`mt-1 font-bold ${disponible <= 0 ? 'text-red-600' : 'text-green-600'}`}>{displayAvailable(item, disponible)}</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Precio venta</p>
          <p className="mt-1 font-semibold text-slate-900">{typeof item.price === 'number' ? moneyCOP(item.price) : '-'}</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Costo unitario</p>
          <p className="mt-1 font-semibold text-slate-900">{typeof item.costPrice === 'number' ? moneyCOP(item.costPrice) : 'N/A'}</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Valor stock</p>
          <p className="mt-1 font-semibold text-slate-900">{moneyCOP(Number(disponible) * Number(item.costPrice || 0))}</p>
        </div>
      </div>
    </article>
  );
};


const handleAdjustmentSubmit = async (e) => {
  e.preventDefault();
  const { productId, quantity, type, reason, user, date, destinationProductId } = adjustmentData;
  const qtyNumber = Number(quantity);

  try {
    if (!productId || !Number.isFinite(qtyNumber) || qtyNumber <= 0 || !reason) {
      alert("Completa todos los campos requeridos.");
      return;
    }

    if (type !== 'transferencia') {
      const prodRef = doc(db, "productos", productId);

      const newStock = await runTransaction(db, async (tx) => {
        const snap = await tx.get(prodRef);
    if (!snap.exists()) throw new Error("Producto no encontrado.");
    const p = snap.data() || {};
    const stockActual = Number(p.stock || 0);
    const qty = qtyNumber;

    let nuevoStock = stockActual;
      if (type === 'ajuste') {
        nuevoStock = qty;                             // stock absoluto
      } else if (type === 'perdida') {
        if (qty > stockActual) throw new Error(`Stock insuficiente. Disponible: ${stockActual}.`);
        nuevoStock = stockActual - qty;
      }

      tx.update(prodRef, { stock: nuevoStock, updatedAt: serverTimestamp() });
    return nuevoStock; // 👈 devolvemos el valor para usarlo afuera
  });

  await addDoc(collection(db, "ajustesInventario"), {
    date,
     createdAt: serverTimestamp(),
user: showUser(user),
    action: type === 'ajuste' ? 'Ajuste de Stock' : 'Pérdida registrada',
    productId,
    productName: products.find(p => p.id === productId)?.name || '',
    type,
    quantity: qtyNumber,
    newStock,        // 👈 ahora es correcto en ambos casos
    reason,
  });

  setProductStocks((prev) => ({ ...prev, [productId]: newStock }));
  if (setProducts) {
    setProducts((prev = []) => prev.map(p => p.id === productId ? { ...p, stock: newStock } : p));
  }

  toast.success(type === 'ajuste'
    ? 'Ajuste de stock aplicado.'
    : 'Pérdida registrada.');
} else {
  if (!destinationProductId || destinationProductId === productId) {
    alert("Selecciona un producto destino válido (diferente al origen).");
    return;
  }

  const origenRef = doc(db, "productos", productId);
  const destinoRef = doc(db, "productos", destinationProductId);

  const txResult = await runTransaction(db, async (tx) => {
    const [snapO, snapD] = await Promise.all([tx.get(origenRef), tx.get(destinoRef)]);
    if (!snapO.exists()) throw new Error("Producto origen no encontrado.");
    if (!snapD.exists()) throw new Error("Producto destino no encontrado.");

    const o = snapO.data() || {};
    const d = snapD.data() || {};

    const qty   = qtyNumber;
    const stockO = Number(o.stock || 0);
    const stockD = Number(d.stock || 0);
    const costO  = Number(o.costPrice || 0);
    const costD  = Number(d.costPrice || 0);

    if (qty <= 0) throw new Error("Cantidad inválida.");
    if (qty > stockO) throw new Error(`Stock insuficiente en origen. Disponible: ${stockO}.`);

    // Origen
    const newStockO = stockO - qty;
    tx.update(origenRef, { stock: newStockO, updatedAt: serverTimestamp() });

    // Destino (promedio ponderado con costo del origen)
    const newStockD = stockD + qty;
    let newAvgD = costD;
    if (newStockD > 0) {
      const totalBefore = costD * stockD;
      const totalAdd    = costO * qty;
      newAvgD = (totalBefore + totalAdd) / newStockD;
    }
    tx.update(destinoRef, { stock: newStockD, costPrice: newAvgD, updatedAt: serverTimestamp() });

    return { newStockO, newStockD, newAvgD };
  });

  await addDoc(collection(db, "ajustesInventario"), {
    date,
     createdAt: serverTimestamp(),
 user: showUser(user),
     action: 'Transferencia',
    productId,
    productName: products.find(p => p.id === productId)?.name || '',
    toProductId: destinationProductId,
    toProductName: products.find(p => p.id === destinationProductId)?.name || '',
    type: 'transferencia',
    quantity: qtyNumber,
    reason,
    // 👇 datos útiles en el historial
    newStockOrigen: txResult.newStockO,
    newStockDestino: txResult.newStockD,
    newAvgDestino: txResult.newAvgD
  });

  setProductStocks((prev) => ({
    ...prev,
    [productId]: txResult.newStockO,
    [destinationProductId]: txResult.newStockD,
  }));
  if (setProducts) {
    setProducts((prev = []) =>
      prev.map(p => {
        if (p.id === productId) return { ...p, stock: txResult.newStockO };
        if (p.id === destinationProductId) return { ...p, stock: txResult.newStockD, costPrice: txResult.newAvgD };
        return p;
      })
    );
  }

  toast.success('Transferencia realizada.');
}

    setShowAdjustmentForm(false);
    setAdjustmentData({
      productId: '',
      quantity: '',
      type: 'ajuste',
      reason: '',
user: showUser(currentUser),
date: localISODate(),
      destinationProductId: ''
    });

    await fetchAjustes();
    // Si quieres, aquí recargas productos

  } catch (error) {
    console.error("❌ Error en el ajuste de inventario:", error);
    alert(error.message || "Ocurrió un error al procesar el ajuste.");
  }
};

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setCurrentSubView('editProduct'); // Cambiar a la vista de edición
  };

const handleDeleteProduct = async (productId) => {
  if (!window.confirm('¿Estás seguro de que quieres eliminar este producto del inventario? Esta acción es irreversible.')) return;

  try {
    // 1) Borrar en Firestore
    await deleteDoc(doc(db, "productos", productId));

    // 2) Actualizar estado local
    setProducts(prev => prev.filter(p => p.id !== productId));
    setProductStocks(prev => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });

    toast.success('Producto eliminado del inventario.');
  } catch (err) {
    console.error('❌ Error eliminando producto:', err);
    toast.error('No se pudo eliminar el producto.');
  }
};

  const handleSaveEditedProduct = (updatedProduct) => {
    setProducts(prev => prev.map(p => p.id === updatedProduct.id ? updatedProduct : p));
    setEditingProduct(null);
    setCurrentSubView('control'); // Volver a la vista de control
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
    setCurrentSubView('control');
  };
const handleDeleteAllAdjustments = async () => {
  if (!window.confirm("¿Seguro que quieres borrar todo el historial de ajustes? Esta acción es irreversible.")) return;
  const querySnapshot = await getDocs(collection(db, "ajustesInventario"));
    const deletePromises = [];
    querySnapshot.forEach((docu) => {
      deletePromises.push(deleteDoc(doc(db, "ajustesInventario", docu.id)));
    });
  await Promise.all(deletePromises);
  await fetchAjustes(); // Recarga la lista de ajustes
  toast.success("Historial de ajustes borrado.");
};

const filteredProducts = (products || []).filter(item =>
  (item.name || '').toLowerCase().includes((searchTerm || '').toLowerCase()) ||
 (item.barcode || '').toLowerCase().includes((searchTerm || '').toLowerCase())
 );

const ajustesPorMes = adjustmentsHistory.reduce((acc, adj) => {
  const fecha = adj.date || '';
  const [year, month, day] = fecha.split('-');
  const mesNombre = new Date(year, month - 1).toLocaleString('es-ES', { month: 'long' });
  const keyMes = `${mesNombre.charAt(0).toUpperCase() + mesNombre.slice(1)} ${year}`; // Ej: "Enero 2024"
  if (!acc[keyMes]) acc[keyMes] = {};
  if (!acc[keyMes][fecha]) acc[keyMes][fecha] = [];
  acc[keyMes][fecha].push(adj);
  return acc;
}, {});
// Despliega todos los ajustes en un array plano
const allAjustes = Object.entries(ajustesPorMes).flatMap(([mes, dias]) =>
  Object.entries(dias).flatMap(([fecha, ajustesDia]) => ajustesDia)
);

// Filtro por usuario y fecha
const ajustesFiltrados = allAjustes.filter(adj =>
(!ajusteFiltroUsuario || showUser(adj.user).toLowerCase().includes(ajusteFiltroUsuario.toLowerCase())) &&
  (!ajusteFiltroFecha || adj.date === ajusteFiltroFecha) &&
  (!ajusteFiltroTipo || adj.type === ajusteFiltroTipo)
);

// Paginación
const totalPaginas = Math.ceil(ajustesFiltrados.length / ajustesPorPagina);
const ajustesPaginados = ajustesFiltrados.slice(
  (paginaAjustes - 1) * ajustesPorPagina,
  paginaAjustes * ajustesPorPagina
);

  const renderSubView = () => {
    switch (currentSubView) {
      case 'control':
        return (
          <>
            {showAdjustmentForm && (
              <div className="mx-auto mb-8 max-w-2xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-xl shadow-slate-200/60">
                <div className="border-b border-slate-200 bg-gradient-to-r from-slate-900 to-sky-700 px-5 py-5">
                <h2 className="text-center text-2xl font-black text-white sm:text-3xl">Realizar Ajuste de Inventario</h2>
                </div>
                <div className="p-4 sm:p-8">
                <form onSubmit={handleAdjustmentSubmit}>
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Producto*</label>
<select
  name="productId"
  value={adjustmentData.productId}
  onChange={(e) => setAdjustmentData({ ...adjustmentData, productId: e.target.value })}
  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
  required
>
  <option value="">Seleccionar producto...</option>

                    {adjustmentOptions.map(product => (
                      <option key={product.id || product.barcode || product.name} value={product.id}>{product.name}</option>
                    ))}
</select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Ajuste*</label>
                        <select
                          name="type"
                          value={adjustmentData.type}
                          onChange={(e) => setAdjustmentData({...adjustmentData, type: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                          required
                        >
                          <option value="ajuste">Ajuste de stock (establecer cantidad)</option>
                          <option value="perdida" disabled={(productStocks[adjustmentData.productId] || 0) <= 0}>
                            Pérdida registrada (restar cantidad)
                          </option>
                          <option value="transferencia" disabled={(productStocks[adjustmentData.productId] || 0) <= 0}>
                            Transferencia (restar cantidad)
                          </option>
                        </select>
                        {(productStocks[adjustmentData.productId] || 0) <= 0 && (
                          <p className="text-xs text-yellow-600 mt-1">
                            No hay stock disponible en este producto, no se permiten pérdidas ni transferencias.
                          </p>
                        )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad*</label>
                      <input
                        type="number"
                        name="quantity"
                        min="1"
                        value={adjustmentData.quantity}
                        onChange={(e) => setAdjustmentData({...adjustmentData, quantity: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Stock actual: {productStocks[adjustmentData.productId] || 0}</p>
                    </div>

                    {adjustmentData.type === 'transferencia' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Producto Destino*</label>
                        <select
                          name="destinationProductId"
                          value={adjustmentData.destinationProductId}
                          onChange={(e) => setAdjustmentData({...adjustmentData, destinationProductId: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                          required
                        >
                          <option value="">Seleccionar producto destino...</option>
 {adjustmentOptions
  .filter(p => p.id !== adjustmentData.productId)
  .map(product => (
   <option key={product.id || product.barcode || product.name} value={product.id}>{product.name}</option>
 ))}                        </select>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Motivo/Razón*</label>
                      <textarea
                        name="reason"
                        value={adjustmentData.reason}
                        onChange={(e) => setAdjustmentData({...adjustmentData, reason: e.target.value})}
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                        required
                      ></textarea>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={() => setShowAdjustmentForm(false)}
                       className="w-full rounded-2xl bg-slate-200 px-6 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-300 transition duration-200 sm:w-auto"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                       className="w-full rounded-2xl bg-sky-600 px-6 py-3 text-sm font-semibold text-white hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 transition duration-200 sm:w-auto"
                    >
                      Registrar Ajuste
                    </button>
                  </div>
                </form>
                </div>
              </div>
            )}

{/* KPIs de inventario */}
<div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
  <div className="bg-white p-6 rounded-xl shadow">
    <h3 className="text-sm text-gray-600">Valor total de inventario</h3>
    <p className="mt-1 text-3xl font-extrabold text-blue-600">{moneyCOP(inventoryTotal)}</p>
    <p className="text-xs text-gray-400 mt-1">Stock × costo unitario (und/g/ml)</p>
  </div>

  <div className="bg-white p-6 rounded-xl shadow">
    <h3 className="text-sm text-gray-600">Ítems contabilizados</h3>
<p className="mt-1 text-3xl font-extrabold text-gray-800">{itemsCount}</p>
    <p className="text-xs text-gray-400 mt-1">Insumos + finales con stock</p>
  </div>

  <div className="bg-white p-6 rounded-xl shadow">
    <h3 className="text-sm text-gray-600">Con costo faltante</h3>
<p className={`mt-1 text-3xl font-extrabold ${missingCost.length ? 'text-red-600' : 'text-green-600'}`}>
  {missingCost.length}
</p>
    <p className="text-xs text-gray-400 mt-1">Productos con stock pero sin costPrice</p>
  </div>
</div>


            <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
              <div className="p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
  <input
    type="text"
    placeholder="Buscar por nombre o codigo"
    value={searchTerm}
    onChange={(e) => { setSearchTerm(e.target.value); setInventoryPage(1); }}
    className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100 lg:max-w-sm"
  />
  <div className="flex items-center gap-2">
    <label htmlFor="inventory-sort" className="sr-only">Ordenar inventario por</label>
    <select
      id="inventory-sort"
      value={inventorySort.key}
      onChange={(event) => setInventorySort((current) => ({ ...current, key: event.target.value }))}
      className="rounded-2xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-700 outline-none focus:border-sky-500"
    >
      <option value="name">Producto</option>
      <option value="barcode">Código</option>
      <option value="available">Disponible</option>
      <option value="price">Precio venta</option>
      <option value="costPrice">Costo unitario</option>
      <option value="stockValue">Valor stock</option>
    </select>
    <button
      type="button"
      onClick={() => setInventorySort((current) => ({ ...current, direction: current.direction === 'asc' ? 'desc' : 'asc' }))}
      className="whitespace-nowrap rounded-2xl border border-sky-200 px-3 py-3 text-sm font-semibold text-sky-700 hover:border-sky-400"
      title="Cambiar dirección del orden"
    >
      {inventorySort.direction === 'asc' ? '↑ Ascendente' : '↓ Descendente'}
    </button>
  </div>
  <div className="flex gap-2 overflow-x-auto pb-1 lg:ml-auto">
    <button
      onClick={() => { setInvTypeFilter('todos'); setInventoryPage(1); }}
      className={`whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold ${invTypeFilter==='todos' ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-700'}`}
    >
      Todos
    </button>
    <button
      onClick={() => { setInvTypeFilter('venta'); setInventoryPage(1); }}
      className={`whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold ${invTypeFilter==='venta' ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-700'}`}
    >
      Para vender ({ventaMostrados.length})
    </button>
    <button
      onClick={() => { setInvTypeFilter('insumos'); setInventoryPage(1); }}
      className={`whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold ${invTypeFilter==='insumos' ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-700'}`}
    >
      Insumos ({insumosContabilizados.length})
    </button>
  </div>
</div>

<div className="mt-4 space-y-4 sm:hidden">
  {(invTypeFilter === 'todos' || invTypeFilter === 'venta') && (
    <section className="space-y-3">
      <div className="rounded-2xl bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-700">
        Productos para vender · {ventaMostrados.length}
      </div>
      {ventaMostrados.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
          Sin productos para vender.
        </div>
      ) : ventaPaginados.length ? (
        ventaPaginados.map((item, idx) => renderMobileInventoryCard(item, `venta-mobile-${idx}-`))
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
          Sin productos para vender en esta pagina.
        </div>
      )}
    </section>
  )}

  {(invTypeFilter === 'todos' || invTypeFilter === 'insumos') && (
    <section className="space-y-3">
      <div className="rounded-2xl bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-700">
        Insumos · {insumosContabilizados.length}
      </div>
      {insumosContabilizados.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
          Sin insumos.
        </div>
      ) : supplyPaginados.length ? (
        supplyPaginados.map((item, idx) => renderMobileInventoryCard(item, `insumo-mobile-${idx}-`))
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
          Sin insumos en esta pagina.
        </div>
      )}
    </section>
  )}
</div>

<div className="mt-4 hidden overflow-x-auto sm:block">
<table className="min-w-full divide-y divide-gray-200">
<thead className="bg-gray-50">
  <tr>
    {renderSortHeader('Producto', 'name')}
    {renderSortHeader('Código', 'barcode')}
    {renderSortHeader('Disponible (und/g/ml)', 'available')}
    {renderSortHeader('Precio venta', 'price')}
    {renderSortHeader('Costo unitario', 'costPrice')}
    {renderSortHeader('Valor stock', 'stockValue')}
   </tr>
</thead>

  {(invTypeFilter === 'todos' || invTypeFilter === 'venta') && (
    <tbody className="bg-white divide-y divide-gray-200">
      <tr className="bg-gray-100 sticky top-0">
 <td colSpan="6" className="px-6 py-2 text-xs font-semibold tracking-wide text-gray-700">
            🛒 Productos para vender · {ventaMostrados.length}
        </td>
      </tr>

      {ventaMostrados.length === 0 ? (
        <tr>
          <td colSpan="6" className="px-6 py-3 text-sm text-gray-400">
            Sin productos para vender.
          </td>
        </tr>
      ) : ventaPaginados.length ? (
        ventaPaginados.map((item, idx) => renderRow(item, `venta-${idx}-`))
      ) : (
        <tr>
          <td colSpan="6" className="px-6 py-3 text-sm text-gray-400">
            Sin productos para vender en esta página.
          </td>
        </tr>
      )}
    </tbody>
  )}

  {(invTypeFilter === 'todos' || invTypeFilter === 'insumos') && (
    <tbody className="bg-white divide-y divide-gray-200">
      <tr className="bg-gray-100">
 <td colSpan="6" className="px-6 py-2 text-xs font-semibold tracking-wide text-gray-700">
            🧰 Insumos · {insumosContabilizados.length}
        </td>
      </tr>

      {insumosContabilizados.length === 0 ? (
        <tr>
          <td colSpan="6" className="px-6 py-3 text-sm text-gray-400">
            Sin insumos.
          </td>
        </tr>
      ) : supplyPaginados.length ? (
        supplyPaginados.map((item, idx) => renderRow(item, `insumo-${idx}-`))
      ) : (
        <tr>
          <td colSpan="6" className="px-6 py-3 text-sm text-gray-400">
            Sin insumos en esta página.
          </td>
        </tr>
      )}
    </tbody>
  )}
</table>
</div>
<div className="flex flex-col gap-3 border-t px-4 py-4 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between sm:px-6">
  <span>
    {totalInventoryItems === 0
      ? 'Sin registros para mostrar.'
      : `Mostrando ${inventoryRangeStart} - ${inventoryRangeEnd} de ${totalInventoryItems} registros`}
  </span>
  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
    <button
      onClick={() => setInventoryPage((page) => Math.max(1, page - 1))}
      disabled={inventoryPage === 1 || totalInventoryItems === 0}
      className={`rounded-2xl px-4 py-3 text-sm font-semibold ${inventoryPage === 1 || totalInventoryItems === 0 ? 'cursor-not-allowed border border-gray-200 text-gray-400' : 'border border-sky-200 text-sky-700 hover:border-sky-400'}`}
    >
      Anterior
    </button>
    <span className="px-3 py-1 text-center">
      {inventoryPage} / {totalInventoryPages}
    </span>
    <button
      onClick={() => setInventoryPage((page) => Math.min(totalInventoryPages, page + 1))}
      disabled={inventoryPage === totalInventoryPages || totalInventoryItems === 0}
      className={`rounded-2xl px-4 py-3 text-sm font-semibold ${inventoryPage === totalInventoryPages || totalInventoryItems === 0 ? 'cursor-not-allowed border border-gray-200 text-gray-400' : 'border border-sky-200 text-sky-700 hover:border-sky-400'}`}
    >
      Siguiente
    </button>
  </div>
</div>
              </div>
            </div>
          </>
        );
      case 'editProduct':
        return (
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl mx-auto transform transition-all duration-300 scale-100 opacity-100">
            <ProductForm
              onSave={handleSaveEditedProduct}
              onCancel={handleCancelEdit}
              currentUser={currentUser}
              categories={['Electrónicos', 'Alimentos', 'Bebidas', 'Limpieza', 'Oficina', 'Hogar']} // Pasar categorías reales
              initialProductData={editingProduct}
            />
          </div>
        );
      case 'physicalInventory':
        return (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden p-6">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Inventario Físico y Comparación</h2>
            {lastPhysicalInventory && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200 text-blue-800">
                <p className="font-semibold">Último Inventario Físico:</p>
                <p>Fecha: {lastPhysicalInventory.date}</p>
                <p>Resultado: {lastPhysicalInventory.result}</p>
              </div>
            )}
            <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="font-semibold text-slate-900">Tipo de conteo</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className={`cursor-pointer rounded-xl border p-3 ${physicalCountMode === 'partial' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'}`}>
                  <input
                    type="radio"
                    name="physicalCountMode"
                    checked={physicalCountMode === 'partial'}
                    onChange={() => { setPhysicalCountMode('partial'); setPhysicalReview(null); }}
                    className="mr-2"
                  />
                  <span className="font-semibold">Conteo parcial</span>
                  <p className="mt-1 text-sm text-slate-600">Solo modifica los productos que tengan una cantidad escrita.</p>
                </label>
                <label className={`cursor-pointer rounded-xl border p-3 ${physicalCountMode === 'complete' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'}`}>
                  <input
                    type="radio"
                    name="physicalCountMode"
                    checked={physicalCountMode === 'complete'}
                    onChange={() => { setPhysicalCountMode('complete'); setPhysicalReview(null); }}
                    className="mr-2"
                  />
                  <span className="font-semibold">Conteo completo</span>
                  <p className="mt-1 text-sm text-slate-600">Exige contar todos los productos antes de continuar.</p>
                </label>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-sm">
                <span className="rounded-full bg-emerald-100 px-3 py-1 font-medium text-emerald-800">
                  Contados: {physicalListUnique.filter((product) => hasOwn(physicalCounts, product.id)).length}
                </span>
                <span className="rounded-full bg-amber-100 px-3 py-1 font-medium text-amber-800">
                  Pendientes: {physicalListUnique.filter((product) => !hasOwn(physicalCounts, product.id)).length}
                </span>
              </div>
            </div>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <input
                type="text"
                value={physicalSearchTerm}
                onChange={(e) => setPhysicalSearchTerm(e.target.value)}
                placeholder="Buscar producto por nombre o código..."
                className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
              />
              <button
                type="button"
                onClick={exportPhysicalComparisonToExcel}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition duration-200 shadow-sm"
              >
                Descargar inventario actual
              </button>
            </div>
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full divide-y divide-gray-200">
<thead className="bg-gray-50">
  <tr>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
   Stock Sistema (und/g/ml)
</th>  
  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Último Conteo Físico</th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conteo Físico Actual</th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Diferencia</th>
  </tr>
</thead>
<tbody className="bg-white divide-y divide-gray-200">
  {filteredPhysicalList.map((product, idx) => (
    <tr key={product.id || product.barcode || `phys-${idx}`} className="hover:bg-gray-50 transition duration-150">
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.name}</td>
<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">
  {formatQtyWithUnit(product, product.stock)}
</td><td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
  {hasOwn(lastPhysicalCountsData, product.id) ? lastPhysicalCountsData[product.id] : 'N/A'}
</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        <input
          type="number"
          min="0"
          value={hasOwn(physicalCounts, product.id) ? physicalCounts[product.id] : ''}
          onChange={(e) => handlePhysicalCountChange(product.id, e.target.value)}
          className="w-24 px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
        />
      </td>
      <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${
        !hasOwn(physicalCounts, product.id)
          ? 'text-amber-600'
          : Number(physicalCounts[product.id]) - Number(product.stock || 0) !== 0
            ? 'text-red-600'
            : 'text-green-600'
      }`}>
        {hasOwn(physicalCounts, product.id)
          ? Number(physicalCounts[product.id]) - Number(product.stock || 0)
          : 'Sin contar'}
      </td>
    </tr>
  ))}
  {filteredPhysicalList.length === 0 && (
    <tr>
      <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
        No se encontraron productos para ese criterio de búsqueda.
      </td>
    </tr>
  )}
</tbody>
              </table>
            </div>
            {physicalReview && (
              <div className={`mb-5 rounded-2xl border p-4 ${physicalReview.zeroCount > 0 ? 'border-red-300 bg-red-50' : 'border-blue-200 bg-blue-50'}`}>
                <h3 className="text-lg font-bold text-slate-900">Revisión antes de aplicar</h3>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                  <div><span className="block text-slate-500">Contados</span><b>{physicalReview.countedCount}</b></div>
                  <div><span className="block text-slate-500">Sin contar</span><b>{physicalReview.pendingCount}</b></div>
                  <div><span className="block text-slate-500">Con cambios</span><b>{physicalReview.differences.length}</b></div>
                  <div><span className="block text-slate-500">Aumentan</span><b className="text-emerald-700">{physicalReview.increaseCount}</b></div>
                  <div><span className="block text-slate-500">Disminuyen</span><b className="text-red-700">{physicalReview.decreaseCount}</b></div>
                  <div><span className="block text-slate-500">Quedan en cero</span><b className={physicalReview.zeroCount ? 'text-red-700' : ''}>{physicalReview.zeroCount}</b></div>
                </div>
                <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                  <p>Sobrante estimado: <b className="text-emerald-700">{moneyCOP(physicalReview.totalSobrante)}</b></p>
                  <p>Faltante estimado: <b className="text-red-700">{moneyCOP(physicalReview.totalFaltante)}</b></p>
                </div>
                {physicalReview.pendingCount > 0 && (
                  <p className="mt-3 rounded-xl bg-white/80 p-3 text-sm font-medium text-amber-800">
                    Los {physicalReview.pendingCount} productos sin contar no serán modificados.
                  </p>
                )}
              </div>
            )}
            <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
              {physicalReview && (
                <button
                  type="button"
                  onClick={() => setPhysicalReview(null)}
                  disabled={savingPhysicalInventory}
                  className="rounded-lg border border-slate-300 px-6 py-2 font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                >
                  Volver a revisar
                </button>
              )}
              <button
                onClick={physicalReview ? confirmPhysicalInventoryAdjustment : handleComparePhysicalInventory}
                disabled={savingPhysicalInventory}
                className={`rounded-lg px-6 py-2 font-semibold text-white shadow-md transition disabled:opacity-60 ${physicalReview ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {savingPhysicalInventory
                  ? 'Aplicando...'
                  : physicalReview
                    ? `Confirmar ${physicalReview.differences.length} ajuste(s)`
                    : 'Comparar antes de ajustar'}
              </button>
            </div>
          </div>
        );
      case 'physicalHistory':
        return (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden p-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Historial de Inventario Físico</h2>
              <button
                onClick={fetchPhysicalHistory}
                disabled={loadingPhysicalHistory}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-60"
              >
                {loadingPhysicalHistory ? 'Cargando...' : 'Actualizar'}
              </button>
              <button
                onClick={exportPhysicalHistoryToCSV}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition"
              >
                Exportar Excel
              </button>
            </div>

            {loadingPhysicalHistory && (
              <div className="py-6 text-blue-600">Cargando historial...</div>
            )}

            {!loadingPhysicalHistory && physicalHistory.length === 0 && (
              <div className="py-6 text-gray-600">No hay eventos registrados.</div>
            )}

            <div className="space-y-4">
              {physicalHistory.map(event => {
                const isOpen = expandedPhysicalEvent === event.id;
                return (
                  <div key={event.id} className="border rounded-xl shadow-sm">
                    <button
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left"
                      onClick={() => setExpandedPhysicalEvent(isOpen ? null : event.id)}
                    >
                      <div>
                        <div className="text-sm text-gray-500">{event.date || 'Sin fecha'}</div>
                        <div className="text-lg font-semibold text-gray-800">{event.user || 'Usuario'}</div>
                        <div className="text-sm text-gray-600">
                          Productos: {(event.items || []).length}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-green-700">
                          Sobrantes: {moneyCOP(event.totalSobrante || 0)}
                        </div>
                        <div className="text-sm text-red-700">
                          Faltantes: {moneyCOP(event.totalFaltante || 0)}
                        </div>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="overflow-auto px-4 pb-4">
                        <table className="min-w-full text-sm text-left text-gray-700 border">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2">Producto</th>
                              <th className="px-3 py-2">Stock sistema</th>
                              <th className="px-3 py-2">Conteo físico</th>
                              <th className="px-3 py-2">Diferencia</th>
                              <th className="px-3 py-2">Costo unit.</th>
                              <th className="px-3 py-2">Valor diferencia</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {(event.items || []).map((item, idx) => (
                              <tr key={`${item.productId || 'row'}-${idx}`}>
                                <td className="px-3 py-2">{item.productName || '-'}</td>
                                <td className="px-3 py-2">{item.systemStock}</td>
                                <td className="px-3 py-2">{item.physicalStock}</td>
                                <td className={`px-3 py-2 font-semibold ${item.difference > 0 ? 'text-green-700' : item.difference < 0 ? 'text-red-700' : 'text-gray-700'}`}>
                                  {item.difference}
                                </td>
                                <td className="px-3 py-2">{moneyCOP(item.unitCost || 0)}</td>
                                <td className="px-3 py-2 font-semibold">
                                  {moneyCOP(item.diffValue || 0)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'adjustments':
        return (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden p-6">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Historial de Ajustes de Stock</h2>
              <button
        onClick={handleDeleteAllAdjustments}
        className="mb-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
      >
        Borrar Todo el Historial de Ajustes
      </button>
      <div className="flex flex-wrap items-center gap-4 mb-4">
  <input
    type="text"
    placeholder="Buscar usuario"
    value={ajusteFiltroUsuario}
    onChange={e => { setAjusteFiltroUsuario(e.target.value); setPaginaAjustes(1); }}
    className="px-4 py-2 border rounded"
    list="lista-usuarios"
  />
  <datalist id="lista-usuarios">
{[...new Set(allAjustes.map(a => showUser(a.user)).filter(Boolean))].map(user => (
        <option key={user} value={user} />
    ))}
  </datalist>
  <input
    type="date"
    placeholder="Filtrar por fecha"
    value={ajusteFiltroFecha}
    onChange={e => { setAjusteFiltroFecha(e.target.value); setPaginaAjustes(1); }}
    className="px-4 py-2 border rounded"
  />
  <select
  value={ajusteFiltroTipo}
  onChange={e => { setAjusteFiltroTipo(e.target.value); setPaginaAjustes(1); }}
  className="px-4 py-2 border rounded"
>
  <option value="">Todos los tipos</option>
  <option value="ajuste">Ajuste</option>
  <option value="perdida">Pérdida</option>
  <option value="transferencia">Transferencia</option>
</select>

</div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nuevo Stock</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Razón</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                  </tr>
                </thead>
<tbody>
  {ajustesPaginados.length === 0 ? (
    <tr>
      <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
        No hay ajustes de stock registrados.
      </td>
    </tr>
  ) : (
    ajustesPaginados.map((adj, index) => (
      <tr key={index} className="hover:bg-gray-50 transition duration-150">
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{adj.date}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{ products.find(p => p.id === adj.productId)?.name}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
          <span className={`px-2 py-1 rounded text-xs font-bold ${
            adj.type === 'ajuste' ? 'bg-green-100 text-green-700' : 
            adj.type === 'perdida' ? 'bg-red-100 text-red-700' : 
            'bg-yellow-100 text-yellow-700'
          }`}>
            {adj.type}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{adj.quantity}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{adj.newStock}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{adj.reason}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{showUser(adj.user)}</td>
      </tr>
    ))
  )}
</tbody>
              </table>
              <div className="flex justify-center items-center mt-4 gap-2">
  <button
    onClick={() => setPaginaAjustes(p => Math.max(1, p - 1))}
    disabled={paginaAjustes === 1}
    className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 mx-1"
  >Anterior</button>
  <span className="px-3 py-1">{paginaAjustes} / {totalPaginas || 1}</span>
  <button
    onClick={() => setPaginaAjustes(p => Math.min(totalPaginas, p + 1))}
    disabled={paginaAjustes === totalPaginas || totalPaginas === 0}
    className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 mx-1"
  >Siguiente</button>
</div>

            </div>
          </div>
        );
      case 'losses':
        return (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden p-6">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Historial de Pérdidas Registradas</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Razón</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {lossesHistory.length > 0 ? (
                    lossesHistory.map((loss, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{loss.date}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{products.find(p => p.id === loss.productId)?.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{loss.quantity}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{loss.reason}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{showUser(loss.user)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                        No hay pérdidas registradas.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="flex justify-center items-center mt-4 gap-2">
  <button
    onClick={() => setPaginaAjustes(p => Math.max(1, p - 1))}
    disabled={paginaAjustes === 1}
    className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 mx-1"
  >Anterior</button>
  <span className="px-3 py-1">{paginaAjustes} / {totalPaginas || 1}</span>
  <button
    onClick={() => setPaginaAjustes(p => Math.min(totalPaginas, p + 1))}
    disabled={paginaAjustes === totalPaginas || totalPaginas === 0}
    className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 mx-1"
  >Siguiente</button>
</div>

            </div>
          </div>
        );
      case 'transfers':
        return (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden p-6">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Historial de Transferencias de Stock</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto Origen</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto Destino</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Razón</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transfersHistory.length > 0 ? (
                    transfersHistory.map((transfer, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transfer.date}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{products.find(p => p.id === transfer.fromProductId)?.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transfer.quantity}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{products.find(p => p.id === transfer.toProductId)?.name || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transfer.reason}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{showUser(transfer.user)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                        No hay transferencias registradas.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50 p-3 sm:p-6">
      <div className="mb-6 rounded-[28px] border border-slate-200 bg-white/95 p-4 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <button
          onClick={onBack}
          className="flex items-center justify-center gap-2 rounded-2xl border border-sky-200 px-4 py-3 text-sm font-semibold text-sky-700 transition hover:border-sky-400 hover:text-sky-900 sm:w-fit"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al Menú
        </button>
        <div className="flex-1 text-center lg:text-left">
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-sky-700">Inventarios</p>
        <h1 className="mt-1 text-2xl font-black text-slate-900 sm:text-4xl">
          {currentSubView === 'control' ? 'Control de Inventario' :
           currentSubView === 'adjustments' ? 'Historial de Ajustes' :
           currentSubView === 'losses' ? 'Historial de Pérdidas' :
           currentSubView === 'transfers' ? 'Historial de Transferencias' :
           currentSubView === 'physicalInventory' ? 'Inventario Físico' :
           currentSubView === 'physicalHistory' ? 'Historial Inv. Físico' :
           'Inventario'}
        </h1>
        </div>
        <div className="flex">
          {currentSubView === 'control' && (
            <button
              onClick={() => setShowAdjustmentForm(true)}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-amber-500 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-600 sm:w-auto"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Ajuste de Inventario
            </button>
          )}
        </div>
        </div>
      </div>

      {negativeStockProducts.length > 0 && (
        <section className="mb-6 rounded-[24px] border border-red-200 bg-red-50 p-4 text-red-950 shadow-sm sm:p-5" role="alert">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-black">
                {negativeStockProducts.length} {negativeStockProducts.length === 1 ? 'producto tiene' : 'productos tienen'} inventario negativo
              </p>
              <p className="mt-1 text-sm text-red-800">
                Revisa entradas o ajustes: {negativeStockProducts.slice(0, 4).map(product => `${product.name} (${Number(product.stock).toLocaleString('es-CO')})`).join(', ')}
                {negativeStockProducts.length > 4 ? ` y ${negativeStockProducts.length - 4} más.` : '.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setCurrentSubView('control');
                setInvTypeFilter('todos');
                setSearchTerm('');
                setInventorySort({ key: 'available', direction: 'asc' });
                setInventoryPage(1);
              }}
              className="shrink-0 rounded-2xl bg-red-700 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-red-800"
            >
              Ver faltantes primero
            </button>
          </div>
        </section>
      )}

      <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
        <button
          onClick={() => setCurrentSubView('control')}
          className={`whitespace-nowrap rounded-2xl px-4 py-3 text-sm font-semibold transition ${currentSubView === 'control' ? 'bg-sky-600 text-white shadow-md' : 'bg-slate-200 text-slate-800 hover:bg-slate-300'}`}
        >
          Control de Inventario
        </button>
        <button
          onClick={() => setCurrentSubView('physicalInventory')}
          className={`whitespace-nowrap rounded-2xl px-4 py-3 text-sm font-semibold transition ${currentSubView === 'physicalInventory' ? 'bg-sky-600 text-white shadow-md' : 'bg-slate-200 text-slate-800 hover:bg-slate-300'}`}
        >
          Inventario Físico
        </button>
        <button
          onClick={() => setCurrentSubView('adjustments')}
          className={`whitespace-nowrap rounded-2xl px-4 py-3 text-sm font-semibold transition ${currentSubView === 'adjustments' ? 'bg-sky-600 text-white shadow-md' : 'bg-slate-200 text-slate-800 hover:bg-slate-300'}`}
        >
          Historial de Ajustes
        </button>
        <button
          onClick={() => setCurrentSubView('physicalHistory')}
          className={`whitespace-nowrap rounded-2xl px-4 py-3 text-sm font-semibold transition ${currentSubView === 'physicalHistory' ? 'bg-sky-600 text-white shadow-md' : 'bg-slate-200 text-slate-800 hover:bg-slate-300'}`}
        >
          Historial Inv. Físico
        </button>
      </div>

      {renderSubView()}
       <ToastContainer position="top-center" autoClose={1500} />
    </div>
  );
};

export default InventoryView;

// DONE
